import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { Camera, ChevronDown, ChevronLeft, Eye, EyeOff, X } from "lucide-react";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import { uploadAvatar } from "../../api/social";
import { AvatarPresetPicker } from "../common/AvatarPresetPicker";
import { AVATAR_PRESETS } from "../../lib/avatarPresets";
import { springs } from "../../lib/motion";
import { InitialsAvatar } from "../Social/PostCard";
import { ReactComponent as LapturaMark } from "../../assets/brand/laptura-mark.svg";
import api from "../../api/axiosInstance";
import { AuthFooter, AuthShell } from "./AuthShell";
import { LegalAgreementStep } from "./LegalAgreementStep";

const fieldClass =
  "relative block w-full appearance-none rounded-lg border border-lap-hairline bg-lap-bg/70 px-3.5 py-2 text-white placeholder-lap-dim transition focus:border-transparent focus:outline-none focus:ring-2 focus:ring-lap-accent sm:py-3 sm:text-sm";

const requiredMark = (
  <span className="text-lap-accent-bright" aria-hidden="true">
    {" *"}
  </span>
);

export default function Register() {
  const [step, setStep] = useState("account");
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    firstName: "",
    lastName: "",
    accessCode: "",
  });
  const [showAccessCode, setShowAccessCode] = useState(false);
  const [avatarPreset, setAvatarPreset] = useState(AVATAR_PRESETS[0].id);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarPicker, setAvatarPicker] = useState(false);
  const avatarFileRef = useRef(null);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const { register, login, updateUser } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const onAvatarFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (avatarPreview) URL.revokeObjectURL(avatarPreview);
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
    setAvatarPreset("");
    setAvatarPicker(false);
  };

  const clearAvatarFile = () => {
    if (avatarPreview) URL.revokeObjectURL(avatarPreview);
    setAvatarFile(null);
    setAvatarPreview(null);
  };

  const pickAvatarPreset = (id) => {
    clearAvatarFile();
    setAvatarPreset(id);
    setAvatarPicker(false);
  };

  const removeAvatar = () => {
    clearAvatarFile();
    setAvatarPreset("");
    setAvatarPicker(false);
  };

  useEffect(() => {
    if (!avatarPicker) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setAvatarPicker(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [avatarPicker]);

  const goToTerms = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    const code = formData.accessCode.trim();
    if (!code) {
      toast.error("Laptura is invite-only. Enter a valid access code to create an account.");
      return;
    }
    try {
      await api.post("/auth/access-code", { code });
      setStep("terms");
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error((typeof detail === "string" && detail) || "That access code isn't recognized.");
    }
  };

  const finishRegister = async (acceptedLegal) => {
    setLoading(true);
    const success = await register({
      username: formData.username,
      email: formData.email,
      password: formData.password,
      name: `${formData.firstName} ${formData.lastName}`.trim() || formData.username,
      avatar_preset: avatarFile ? undefined : avatarPreset || undefined,
      accepted_legal: acceptedLegal,
      access_code: formData.accessCode.trim(),
    });

    if (!success) {
      setLoading(false);
      return;
    }

    if (avatarFile) {
      const loggedIn = await login(formData.username, formData.password);
      if (loggedIn) {
        try {
          const { avatar_url } = await uploadAvatar(avatarFile);
          updateUser({ avatar_url });
        } catch {
          toast.error("Account created, but the photo didn't upload. Set it from your profile.");
        }
        navigate("/Laptura/dashboard");
        setLoading(false);
        return;
      }
    }

    navigate("/Laptura/login");
    setLoading(false);
  };

  const displayName =
    `${formData.firstName} ${formData.lastName}`.trim() || formData.username || "You";

  return (
    <AuthShell>
      <div className="w-full min-w-0 max-w-lg lg:rounded-2xl lg:border lg:border-lap-hairline lg:bg-lap-card/45 lg:p-8 lg:shadow-2xl lg:shadow-black/25 xl:p-10">
        {step === "terms" && (
          <button
            type="button"
            onClick={() => setStep("account")}
            className="glass rounded-full w-11 h-11 flex items-center justify-center text-lap-text mb-4"
            aria-label="Back to account details"
            data-testid="register-back"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
        )}
        <div>
          <div className="flex justify-center">
            <LapturaMark className="h-10 w-auto text-lap-text sm:h-16" role="img" aria-label="Laptura" />
          </div>
          <p className="mt-4 text-center text-xs font-semibold uppercase tracking-[0.24em] text-lap-accent-bright sm:mt-6 lg:mt-7">
            {step === "account" ? "Driver registration" : "Final step"}
          </p>
          <h2 className="mt-2 text-center text-2xl sm:text-3xl font-extrabold text-white">
            {step === "account" ? "Create your account" : "Terms and waiver"}
          </h2>
          {step === "account" && (
            <p className="mt-2 text-center text-sm text-lap-muted">
              Already have an account?{" "}
              <Link
                to="/Laptura/login"
                className="font-medium text-lap-accent-bright hover:text-lap-accent-bright"
              >
                Sign in
              </Link>
            </p>
          )}
        </div>

        {step === "account" ? (
          <form className="mt-4 space-y-3 sm:mt-6 sm:space-y-4" onSubmit={goToTerms} data-testid="register-form">
            <div className="space-y-3 sm:space-y-4">
              <div data-testid="register-avatar-picker" className="flex flex-col items-center">
                <p className="block text-sm font-medium text-lap-text mb-2">Profile picture</p>
                <div className="relative inline-block">
                  <InitialsAvatar
                    name={displayName}
                    avatarUrl={avatarPreview || (avatarPreset ? `preset:${avatarPreset}` : null)}
                    isMe
                    size={56}
                    onClick={() => setAvatarPicker(true)}
                    ariaLabel="Change profile picture"
                    testId="register-avatar-preview"
                  />
                  <span
                    className="absolute -bottom-0.5 -right-0.5 w-6 h-6 rounded-full bg-lap-raised border border-lap-hairline flex items-center justify-center text-lap-muted pointer-events-none"
                    aria-hidden
                  >
                    <Camera className="w-3 h-3" />
                  </span>
                </div>
                <input
                  ref={avatarFileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={onAvatarFile}
                  data-testid="register-avatar-file"
                />
                {avatarPicker &&
                  createPortal(
                    <motion.div
                      className="fixed inset-0 z-[80] flex items-end md:items-center justify-center bg-lap-bg/60 p-0 md:p-6"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      onClick={() => setAvatarPicker(false)}
                      data-testid="register-avatar-sheet"
                    >
                      <motion.div
                        className="glass rounded-t-sheet md:rounded-card w-full md:max-w-md p-5 max-h-[85dvh] overflow-y-auto pb-[max(1.25rem,env(safe-area-inset-bottom,0px))]"
                        initial={{ y: 48, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={springs.content}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex items-center justify-between mb-4">
                          <h2 className="text-lg font-extrabold text-lap-text">Profile picture</h2>
                          <button
                            type="button"
                            onClick={() => setAvatarPicker(false)}
                            className="w-9 h-9 rounded-full flex items-center justify-center text-lap-muted hover:bg-lap-raised"
                            aria-label="Close"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <p className="text-sm text-lap-dim mb-3 text-left">Choose an icon</p>
                        <AvatarPresetPicker
                          className="mb-5"
                          value={avatarPreset || null}
                          onChange={pickAvatarPreset}
                          testIdPrefix="register-avatar"
                        />
                        <div className="flex flex-col gap-2">
                          <button
                            type="button"
                            onClick={() => avatarFileRef.current?.click()}
                            className="w-full h-11 rounded-full bg-lap-accent text-white text-sm font-semibold"
                            data-testid="register-avatar-upload"
                          >
                            Upload your own photo
                          </button>
                          {(avatarFile || avatarPreset) && (
                            <button
                              type="button"
                              onClick={removeAvatar}
                              className="w-full h-11 rounded-full text-sm font-semibold text-lap-loss hover:bg-lap-loss/10"
                              data-testid="register-avatar-remove"
                            >
                              Remove picture
                            </button>
                          )}
                        </div>
                      </motion.div>
                    </motion.div>,
                    document.body
                  )}
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-4">
                <div>
                  <label htmlFor="firstName" className="mb-1 block text-sm font-medium text-lap-text sm:mb-2">
                    First Name
                  </label>
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    className={fieldClass}
                    placeholder="First name"
                    value={formData.firstName}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="mb-1 block text-sm font-medium text-lap-text sm:mb-2">
                    Last Name
                  </label>
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    className={fieldClass}
                    placeholder="Last name"
                    value={formData.lastName}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-4">
                <div>
                <label htmlFor="username" className="mb-1 block text-sm font-medium text-lap-text sm:mb-2">
                  Username{requiredMark}
                </label>
                <input
                  id="username"
                  name="username"
                  type="text"
                  required
                  className={fieldClass}
                  placeholder="Choose a username"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  value={formData.username}
                  onChange={handleChange}
                />
                </div>

                <div>
                <label htmlFor="email" className="mb-1 block text-sm font-medium text-lap-text sm:mb-2">
                  Email Address{requiredMark}
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  className={fieldClass}
                  placeholder="you@example.com"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  value={formData.email}
                  onChange={handleChange}
                />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-4">
                <div>
                <label htmlFor="password" className="mb-1 block text-sm font-medium text-lap-text sm:mb-2">
                  Password{requiredMark}
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    required
                    className={`${fieldClass} pr-10`}
                    placeholder="Create a password"
                    value={formData.password}
                    onChange={handleChange}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-lap-muted hover:text-lap-text" />
                    ) : (
                      <Eye className="h-5 w-5 text-lap-muted hover:text-lap-text" />
                    )}
                  </button>
                </div>
                </div>

                <div>
                <label htmlFor="confirmPassword" className="mb-1 block text-sm font-medium text-lap-text sm:mb-2">
                  Confirm Password{requiredMark}
                </label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  required
                  className={fieldClass}
                  placeholder="Confirm your password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                />
                </div>
              </div>

              <div>
                <button
                  type="button"
                  onClick={() => setShowAccessCode((open) => !open)}
                  className="flex items-center gap-1 text-sm text-lap-dim hover:text-lap-text"
                  aria-expanded={showAccessCode}
                  data-testid="register-access-code-toggle"
                >
                  Have an access code?
                  <ChevronDown className={`w-4 h-4 transition-transform ${showAccessCode ? "rotate-180" : ""}`} />
                </button>
                {showAccessCode && (
                  <input
                    id="accessCode"
                    name="accessCode"
                    type="text"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    aria-label="Access code"
                    className={`${fieldClass} mt-2`}
                    placeholder="Access code"
                    value={formData.accessCode}
                    onChange={handleChange}
                    data-testid="register-access-code"
                  />
                )}
              </div>

            </div>

            <button
              type="submit"
              className="flex w-full justify-center rounded-lg bg-lap-accent px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-lap-pressed focus:outline-none focus:ring-2 focus:ring-lap-accent focus:ring-offset-2 focus:ring-offset-lap-bg"
            >
              Continue to terms and waiver
            </button>
          </form>
        ) : (
          <div className="mt-8">
            <LegalAgreementStep
              onSigned={finishRegister}
              submitting={loading}
              submitLabel="Create account"
            />
          </div>
        )}

        <AuthFooter />
      </div>
    </AuthShell>
  );
}
